import headerBackground from './headerBackground.svg'
import {
  IntegrationConfig,
  ResolvedIntegration
} from 'modules/Integrations/domain'
import {
  Badge,
  Button,
  GreyGrey30,
  GreyGrey70,
  GreyGrey90,
  GreyWhite,
  IconBookOpen,
  IconClock,
  IconSettingsGear,
  Image,
  Primary13Regular,
  Primary16SemiBold,
  Spinner,
  useModalClose,
  Box,
  Flex,
  Grid,
  GridItem
} from '@sequencehq/core-components'
import { useIntegrationDetailHeader } from './useIntegrationDetailHeader'
import { ProtectedButton } from 'lib/rbac'

type IntegrationDetailHeaderProps = {
  integration: ResolvedIntegration
}

const IntegrationDetailHeaderPill = ({
  statusLabel,
  state
}: {
  statusLabel: IntegrationConfig['statusLabel']
  state: ResolvedIntegration['state']
}) => {
  const showComingSoonPill =
    statusLabel === 'COMING_SOON' &&
    !['AVAILABLE', 'PENDING', 'CONNECTED'].includes(state)

  if (showComingSoonPill) {
    return (
      <Badge
        data-testid="integrationsBrowser.detail.comingSoon"
        sentiment="neutral"
      >
        <Flex gap="4px">
          <IconClock />
          Coming soon
        </Flex>
      </Badge>
    )
  }
  if (statusLabel === 'BETA') {
    return (
      <Badge sentiment="info">
        <Flex gap="4px">Beta</Flex>
      </Badge>
    )
  }

  return null
}

export const IntegrationDetailHeader = (
  props: IntegrationDetailHeaderProps
) => {
  const closeModal = useModalClose()
  const hook = useIntegrationDetailHeader({ ...props, onClose: closeModal })

  return (
    <Grid
      width="100%"
      padding="20px"
      minHeight="132px"
      backgroundImage={headerBackground}
      gridTemplateColumns="48px 1fr auto"
      gridTemplateRows="1fr auto"
      gridTemplateAreas={`
        "logo description badges"
        "empty actions actions"
      `}
      gridColumnGap="16px"
      gridRowGap="12px"
      borderBottom={`1px solid ${GreyGrey30}`}
    >
      <GridItem gridArea="badges">
        <IntegrationDetailHeaderPill
          statusLabel={hook.statusLabel}
          state={props.integration.state}
        />
      </GridItem>
      <GridItem
        gridArea="logo"
        display="flex"
        justifyContent="center"
        paddingTop="6px"
        alignItems="flex-start"
        height="48px"
      >
        <Image
          src={props.integration.logoUrl}
          width="100%"
          height="100%"
          maxHeight="48px"
        />
      </GridItem>
      <GridItem gridArea="description">
        <Box {...Primary16SemiBold} color={GreyGrey90}>
          {props.integration.title}
        </Box>
        <Box {...Primary13Regular} color={GreyGrey70}>
          {props.integration.description}
        </Box>
      </GridItem>
      <GridItem gridArea="actions" display="flex" gap="8px">
        <Button
          variant="secondary"
          onClick={hook.features.documentation.onClick}
          disabled={hook.features.documentation.disabled}
        >
          <Flex gap="8px">
            <IconBookOpen />
            Documentation
          </Flex>
        </Button>
        {hook.features.connect.visible && (
          <ProtectedButton permission="org.integration:create">
            <Button
              data-testid={`integrationsBrowser.detail.${props.integration.id}.connect`}
              variant="primary"
              disabled={hook.features.connect.disabled}
              onClick={hook.features.connect.onClick}
              leadingIcon={
                hook.features.connect.showSpinner ? (
                  <Spinner height="16px" width="16px" color={GreyWhite} />
                ) : undefined
              }
            >
              {hook.features.connect.label}
            </Button>
          </ProtectedButton>
        )}
        {hook.features.manage.visible && (
          <ProtectedButton permission="org.integration:update">
            <Button
              data-testid={`integrationsBrowser.detail.${props.integration.id}.manage`}
              variant="secondary"
              onClick={hook.features.manage.onClick}
              onAuxClick={hook.features.manage.onAuxClick}
            >
              <Flex gap="8px">
                <IconSettingsGear />
                {hook.features.manage.label}
              </Flex>
            </Button>
          </ProtectedButton>
        )}
      </GridItem>
    </Grid>
  )
}
